
/**
 * Prayer Requests Management Hook
 *
 * This hook provides comprehensive state management for prayer requests
 * in the moderation dashboard. It handles:
 *   - Fetching paginated prayer lists with filters
 *   - Real-time updates via WebSocket
 *   - CRUD operations (update, dismiss, restore, mark answered)
 *   - Client-side search filtering
 *
 * Features:
 *   - Automatic pagination with customizable page size
 *   - Filter by status, type, category, and date range
 *   - Real-time updates when new prayers arrive
 *   - Optimistic UI updates for better UX
 *
 * @example
 * function ModerationPage() {
 *   const {
 *     prayers,
 *     isLoading,
 *     filters,
 *     setFilters,
 *     dismissPrayer
 *   } = usePrayerRequests({
 *     initialFilters: { status: 'pending' },
 *     pageSize: 20
 *   });
 *
 *   // Render prayer list...
 * }
 *
 * @module hooks/usePrayerRequests
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { api } from '@/lib/api';
import { getPrayerCategories } from '@/lib/categories';
import { PrayerRequest, PrayerStatus, PrayerType, UpdatePrayerRequest } from '@/types';
import { useWebSocket, WSEventType, WSMessage } from './useWebSocket';
import type { FilterState, VideoResponseFilter } from '@/components/moderation/FilterBar';

/** API response shape for paginated prayer list */
interface PrayerRequestListResponse {
  items: PrayerRequest[];
  total: number;
  skip: number;
  limit: number;
}

/**
 * Configuration options for usePrayerRequests hook.
 *
 * @property initialFilters - Default filter values on mount
 * @property pageSize - Items per page (default: 20)
 * @property autoFetch - Fetch on mount and filter change (default: true)
 * @property enableRealtime - Subscribe to WebSocket updates (default: true)
 * @property token - JWT token for authenticated requests
 */
interface UsePrayerRequestsOptions {
  initialFilters?: Partial<FilterState>;
  pageSize?: number;
  autoFetch?: boolean;
  enableRealtime?: boolean;
  token?: string;
}

/**
 * Return type for usePrayerRequests hook.
 */
interface UsePrayerRequestsReturn {
  /** Array of prayer requests for current page */
  prayers: PrayerRequest[];
  /** Total number of prayers matching filters */
  total: number;
  /** Current page number (1-indexed) */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Whether prayers are being loaded */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Current filter state */
  filters: FilterState;
  /** Update filters (resets to page 1) */
  setFilters: (filters: FilterState) => void;
  /** Navigate to specific page */
  setPage: (page: number) => void;
  /** Manually refresh prayer list */
  refresh: () => Promise<void>;
  /** Update a prayer's data */
  updatePrayer: (id: string, data: UpdatePrayerRequest) => Promise<boolean>;
  /** Dismiss (archive) a prayer */
  dismissPrayer: (id: string) => Promise<boolean>;
  /** Approve and add to live queue (optimistic remove from list) */
  queuePrayer: (id: string, queueType: 'prayer' | 'praise') => Promise<boolean>;
  /** Send to the stage queue and mark displayed */
  sendToStage: (id: string) => Promise<boolean>;
  /** Restore an archived prayer */
  restorePrayer: (id: string) => Promise<boolean>;
  /** Prayer IDs currently being approved (disable double-click) */
  queuingIds: ReadonlySet<string>;
  /** Prayer IDs currently being sent to stage */
  stagingIds: ReadonlySet<string>;
  /** Whether an update operation is in progress */
  isUpdating: boolean;
  /** Prayer ID -> { userId, userName } for who is currently editing (real-time) */
  editingBy: Record<string, { userId: string; userName: string }>;
  /** Prayer ID -> { userId, userName } for who is currently responding (real-time) */
  respondingBy: Record<string, { userId: string; userName: string }>;
  /** Send a raw WebSocket message (e.g. start_editing / stop_editing) */
  sendMessage: (message: object) => void;
  /** Moderator: send a pending prayer to prayer partners for a response (text or video) */
  requestResponse: (id: string) => Promise<boolean>;
  /** Moderator: flag submission as inappropriate (does not dismiss) */
  reportPrayer: (id: string, reason?: string) => Promise<boolean>;
}

/** Default filter values */
const defaultFilters: FilterState = {
  status: 'all',
  videoResponseStatus: 'all',
  type: 'all',
  category: 'all',
  dateRange: 'all',
  search: '',
  sortOrder: 'asc',
};

function statusMatchesFilter(
  prayerStatus: PrayerStatus,
  filterStatus: FilterState['status']
): boolean {
  if (filterStatus === 'all') return true;
  return prayerStatus === filterStatus;
}

function videoResponseMatchesFilter(
  prayer: PrayerRequest,
  filter: VideoResponseFilter
): boolean {
  if (filter === 'all') return true;
  return prayer.videoResponseStatus === filter;
}

/**
 * Hook for fetching and managing prayer requests.
 *
 * Provides complete state management for the moderation dashboard
 * including filtering, pagination, and real-time updates.
 *
 * @param options - Configuration options
 * @returns Prayer state and management functions
 */
export function usePrayerRequests(
  options: UsePrayerRequestsOptions = {}
): UsePrayerRequestsReturn {
  const {
    initialFilters = {},
    pageSize = 20,
    autoFetch = true,
    enableRealtime = true,
    token,
  } = options;

  const [prayers, setPrayers] = useState<PrayerRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingBy, setEditingBy] = useState<Record<string, { userId: string; userName: string }>>({});
  const [respondingBy, setRespondingBy] = useState<Record<string, { userId: string; userName: string }>>({});
  const [queuingIds, setQueuingIds] = useState<Set<string>>(() => new Set());
  const [stagingIds, setStagingIds] = useState<Set<string>>(() => new Set());
  // Memoize initial filters merge to prevent object recreation
  // Only compute once on mount, don't recompute when initialFilters changes
  const initialFiltersMemo = useMemo(() => ({
    ...defaultFilters,
    ...initialFilters,
  }), []); // Empty deps - only compute once

  const [filters, setFilters] = useState<FilterState>(initialFiltersMemo);

  const mountedRef = useRef(true);
  const fetchingRef = useRef(false);
  const fetchPrayersRef = useRef<((options?: { silent?: boolean }) => Promise<void>) | null>(null);
  // Backup state for optimistic actions (for rollback on error)
  const optimisticBackupRef = useRef<Map<string, { prayers?: PrayerRequest[]; total?: number }>>(
    new Map()
  );
  /** IDs removed by successful approve; filters stale GET responses until server omits them */
  const locallyRemovedIdsRef = useRef<Set<string>>(new Set());

  // Calculate date range for API
  const getDateRange = useCallback(() => {
    const now = new Date();
    let dateFrom: string | undefined;
    let dateTo: string | undefined;

    switch (filters.dateRange) {
      case 'today':
        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0);
        dateFrom = todayStart.toISOString();
        break;
      case 'week':
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        dateFrom = weekAgo.toISOString();
        break;
      case 'month':
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        dateFrom = monthAgo.toISOString();
        break;
      case 'custom':
        dateFrom = filters.dateFrom;
        dateTo = filters.dateTo;
        break;
    }

    return { dateFrom, dateTo };
  }, [filters.dateRange, filters.dateFrom, filters.dateTo]);

  // Build query parameters
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    const skip = (page - 1) * pageSize;

    params.set('skip', skip.toString());
    params.set('limit', pageSize.toString());

    if (filters.status !== 'all') {
      params.set('status', filters.status);
    }
    if (filters.videoResponseStatus !== 'all') {
      params.set('video_response_status', filters.videoResponseStatus);
    }
    if (filters.type !== 'all') {
      params.set('type', filters.type);
    }
    if (filters.category !== 'all') {
      params.set('category', filters.category);
    }

    const { dateFrom, dateTo } = getDateRange();
    if (dateFrom) {
      params.set('date_from', dateFrom);
    }
    if (dateTo) {
      params.set('date_to', dateTo);
    }

    if (filters.sortOrder) {
      params.set('order', filters.sortOrder);
    }

    if (filters.search?.trim()) {
      params.set('search', filters.search.trim());
    }

    return params.toString();
  }, [page, pageSize, filters, getDateRange]);

  // Fetch prayers from API
  const fetchPrayers = useCallback(async (options?: { silent?: boolean }) => {
    // Prevent concurrent fetches
    if (fetchingRef.current) return;
    
    fetchingRef.current = true;
    if (!options?.silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const queryString = buildQueryParams();
      const response = await api.get<PrayerRequestListResponse>(
        `/prayer-requests?${queryString}`
      );

      if (!mountedRef.current) return;

      if (response.error) {
        setError(response.error);
        return;
      }

      if (response.data) {
        const rawItems = response.data.items;
        const removed = locallyRemovedIdsRef.current;

        for (const id of [...removed]) {
          if (!rawItems.some((p) => p.id === id)) {
            removed.delete(id);
          }
        }

        const items = rawItems.filter((p) => !removed.has(p.id));

        // Search is applied server-side; no client-side filtering

        // Only update if data actually changed to prevent unnecessary re-renders
        setPrayers((prev) => {
          // Simple comparison: if same length and all IDs match in same order, don't update
          if (prev.length === items.length && 
              prev.every((p, i) => p.id === items[i]?.id)) {
            // Check if any item actually changed
            const hasChanges = prev.some((p, i) => {
              const newItem = items[i];
              return (
                !newItem ||
                p.status !== newItem.status ||
                p.body !== newItem.body ||
                (p.reportCount ?? 0) !== (newItem.reportCount ?? 0)
              );
            });
            if (!hasChanges) {
              return prev; // No change, return previous array
            }
          }
          return items;
        });
        setTotal(response.data.total);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch prayers');
      }
    } finally {
      fetchingRef.current = false;
      if (mountedRef.current && !options?.silent) {
        setIsLoading(false);
      }
    }
  }, [buildQueryParams, filters.search]);

  // Update a prayer
  const updatePrayer = useCallback(
    async (id: string, data: UpdatePrayerRequest): Promise<boolean> => {
      setIsUpdating(true);
      try {
        const response = await api.put<PrayerRequest>(`/prayer-requests/${id}`, data);

        if (response.error) {
          setError(response.error);
          return false;
        }

        if (response.data) {
          // Optimistic update
          setPrayers((prev) =>
            prev.map((p) => (p.id === id ? { ...p, ...response.data } : p))
          );
        }

        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update prayer');
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  // Dismiss a prayer
  const dismissPrayer = useCallback(async (id: string): Promise<boolean> => {
    setIsUpdating(true);

    // Optimistic UI: remove immediately so the card disappears right away.
    setPrayers((prev) => {
      optimisticBackupRef.current.set(id, { prayers: prev });
      return prev.filter((p) => p.id !== id);
    });
    setTotal((prevTotal) => {
      const existing = optimisticBackupRef.current.get(id) || {};
      if (existing.total === undefined) {
        optimisticBackupRef.current.set(id, { ...existing, total: prevTotal });
      }
      return Math.max(0, prevTotal - 1);
    });

    try {
      const response = await api.post<PrayerRequest>(`/prayer-requests/${id}/dismiss`);

      if (response.error) {
        setError(response.error);
        // Roll back optimistic update
        const backup = optimisticBackupRef.current.get(id);
        if (backup?.prayers) setPrayers(backup.prayers);
        if (backup?.total !== undefined) setTotal(backup.total);
        return false;
      }

      if (response.data) {
        // Prayer is archived server-side; we already removed it from the list.
        // If the user switches to "archived", it will appear on next fetch.
      }

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to dismiss prayer');
      // Roll back optimistic update
      const backup = optimisticBackupRef.current.get(id);
      if (backup?.prayers) setPrayers(backup.prayers);
      if (backup?.total !== undefined) setTotal(backup.total);
      return false;
    } finally {
      optimisticBackupRef.current.delete(id);
      setIsUpdating(false);
    }
  }, []);

  const queuePrayer = useCallback(
    async (id: string, queueType: 'prayer' | 'praise'): Promise<boolean> => {
      setQueuingIds((prev) => new Set(prev).add(id));
      setIsUpdating(true);

      setPrayers((prev) => {
        optimisticBackupRef.current.set(id, { prayers: prev });
        return prev.filter((p) => p.id !== id);
      });
      setTotal((prevTotal) => {
        const existing = optimisticBackupRef.current.get(id) || {};
        if (existing.total === undefined) {
          optimisticBackupRef.current.set(id, { ...existing, total: prevTotal });
        }
        return Math.max(0, prevTotal - 1);
      });

      try {
        const response = await api.post<unknown>('/live-queue/add', {
          prayerRequestId: id,
          queueType,
        });

        if (response.status === 409) {
          locallyRemovedIdsRef.current.add(id);
          setError(null);
          return true;
        }

        if (response.error) {
          setError(response.error);
          const backup = optimisticBackupRef.current.get(id);
          if (backup?.prayers) setPrayers(backup.prayers);
          if (backup?.total !== undefined) setTotal(backup.total);
          return false;
        }

        locallyRemovedIdsRef.current.add(id);
        setError(null);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add to queue');
        const backup = optimisticBackupRef.current.get(id);
        if (backup?.prayers) setPrayers(backup.prayers);
        if (backup?.total !== undefined) setTotal(backup.total);
        return false;
      } finally {
        optimisticBackupRef.current.delete(id);
        setQueuingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setIsUpdating(false);
      }
    },
    []
  );

  const sendToStage = useCallback(
    async (id: string): Promise<boolean> => {
      setStagingIds((prev) => new Set(prev).add(id));
      setIsUpdating(true);

      const removeFromList =
        filters.status !== 'all' && filters.status !== PrayerStatus.DISPLAYED;

      if (removeFromList) {
        setPrayers((prev) => {
          optimisticBackupRef.current.set(id, { prayers: prev });
          return prev.filter((p) => p.id !== id);
        });
        setTotal((prevTotal) => {
          const existing = optimisticBackupRef.current.get(id) || {};
          if (existing.total === undefined) {
            optimisticBackupRef.current.set(id, { ...existing, total: prevTotal });
          }
          return Math.max(0, prevTotal - 1);
        });
      } else {
        setPrayers((prev) => {
          optimisticBackupRef.current.set(id, { prayers: prev });
          return prev.map((p) =>
            p.id === id ? { ...p, status: PrayerStatus.DISPLAYED, onStage: true } : p
          );
        });
      }

      const rollback = () => {
        const backup = optimisticBackupRef.current.get(id);
        if (backup?.prayers) setPrayers(backup.prayers);
        if (backup?.total !== undefined) setTotal(backup.total);
      };

      try {
        const response = await api.post<unknown>('/stage-queue/add', {
          prayerRequestId: id,
        });

        if (response.status === 409) {
          if (removeFromList) {
            locallyRemovedIdsRef.current.add(id);
          }
          setError(null);
          return true;
        }

        if (response.error) {
          setError(response.error);
          rollback();
          return false;
        }

        if (removeFromList) {
          locallyRemovedIdsRef.current.add(id);
        }
        setError(null);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send to stage');
        rollback();
        return false;
      } finally {
        optimisticBackupRef.current.delete(id);
        setStagingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setIsUpdating(false);
      }
    },
    [filters.status]
  );

  // Send pending prayer to prayer partners (moderator+)
  const requestResponse = useCallback(async (id: string): Promise<boolean> => {
    setIsUpdating(true);
    try {
      const response = await api.post<PrayerRequest>(
        `/prayer-requests/${id}/request-response`
      );

      if (response.error) {
        setError(response.error);
        return false;
      }

      if (response.data) {
        setPrayers((prev) =>
          prev.map((p) => (p.id === id ? { ...p, ...response.data } : p))
        );
      }

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request response');
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const reportPrayer = useCallback(
    async (id: string, reason?: string): Promise<boolean> => {
      setIsUpdating(true);
      try {
        const response = await api.post<PrayerRequest>(
          `/prayer-requests/${id}/report`,
          reason ? { reason } : {}
        );

        if (response.error) {
          setError(response.error);
          return false;
        }

        if (response.data) {
          setPrayers((prev) =>
            prev.map((p) => (p.id === id ? { ...p, ...response.data } : p))
          );
        }

        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to report prayer');
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  // Restore a dismissed prayer
  const restorePrayer = useCallback(async (id: string): Promise<boolean> => {
    setIsUpdating(true);
    try {
      const response = await api.post<PrayerRequest>(`/prayer-requests/${id}/restore`);

      if (response.error) {
        setError(response.error);
        return false;
      }

      if (response.data) {
        setPrayers((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, status: PrayerStatus.PENDING } : p
          )
        );
      }

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restore prayer');
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, []);


  // Handle WebSocket messages for real-time updates
  // Use refs to access current filters/pagination without causing callback recreation
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Track latest page/total so the memoized WS handler can make
  // pagination-aware decisions without being recreated on every change.
  const pageRef = useRef(page);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const totalRef = useRef(total);
  useEffect(() => {
    totalRef.current = total;
  }, [total]);

  const handleWebSocketMessage = useCallback(
    (message: WSMessage) => {
      if (!mountedRef.current) return;

      switch (message.event) {
        case WSEventType.NEW_PRAYER: {
          // A brand-new prayer has the latest created_at, so it always
          // belongs at the end of the last page (asc) or the top of page 1
          // (desc). Insert into the local list only when the moderator is
          // actually viewing the page where it would land; otherwise just
          // bump total so totalPages grows and they can navigate to it.
          const newPrayer = message.data as PrayerRequest;
          const currentFiltersForNew = filtersRef.current;
          const matchesStatus = statusMatchesFilter(
            newPrayer.status,
            currentFiltersForNew.status
          );
          const matchesVideo = videoResponseMatchesFilter(
            newPrayer,
            currentFiltersForNew.videoResponseStatus
          );
          const matchesType =
            currentFiltersForNew.type === 'all' || newPrayer.type === currentFiltersForNew.type;
          const matchesCategory =
            currentFiltersForNew.category === 'all' ||
            getPrayerCategories(newPrayer).includes(currentFiltersForNew.category);
          const matchesFilter =
            matchesStatus && matchesVideo && matchesType && matchesCategory;

          if (!matchesFilter) break;

          const sortOrder = currentFiltersForNew.sortOrder ?? 'asc';
          const currentPage = pageRef.current;
          const newTotal = totalRef.current + 1;
          const lastPage = Math.max(1, Math.ceil(newTotal / pageSize));

          setTotal(newTotal);

          if (sortOrder === 'asc') {
            setPrayers((prev) =>
              currentPage === lastPage && prev.length < pageSize
                ? [...prev, newPrayer]
                : prev
            );
          } else {
            setPrayers((prev) => {
              if (currentPage !== 1) return prev;
              const next = [newPrayer, ...prev];
              return next.length > pageSize ? next.slice(0, pageSize) : next;
            });
          }
          break;
        }

        case WSEventType.PRAYER_UPDATED:
          // Update the prayer in the list, add if it newly matches filters, or remove if not
          const updatedPrayer = message.data as PrayerRequest;
          const currentFiltersForUpdated = filtersRef.current;

          // Search is server-side; re-fetch when active so list stays consistent
          if (currentFiltersForUpdated.search?.trim()) {
            fetchPrayersRef.current?.({ silent: true });
            break;
          }

          const matchesStatusUpdated = statusMatchesFilter(
            updatedPrayer.status,
            currentFiltersForUpdated.status
          );
          const matchesVideoUpdated = videoResponseMatchesFilter(
            updatedPrayer,
            currentFiltersForUpdated.videoResponseStatus
          );
          const matchesTypeUpdated =
            currentFiltersForUpdated.type === 'all' ||
            updatedPrayer.type === currentFiltersForUpdated.type;
          const matchesCategoryUpdated =
            currentFiltersForUpdated.category === 'all' ||
            getPrayerCategories(updatedPrayer).includes(currentFiltersForUpdated.category);
          const matchesFilters =
            matchesStatusUpdated &&
            matchesVideoUpdated &&
            matchesTypeUpdated &&
            matchesCategoryUpdated;

          let totalDelta = 0;
          setPrayers((prev) => {
            const exists = prev.some((p) => p.id === updatedPrayer.id);

            if (matchesFilters) {
              if (exists) {
                return prev.map((p) =>
                  p.id === updatedPrayer.id ? updatedPrayer : p
                );
              }
              totalDelta = 1;
              const sortOrder = currentFiltersForUpdated.sortOrder ?? 'asc';
              return sortOrder === 'desc'
                ? [updatedPrayer, ...prev]
                : [...prev, updatedPrayer];
            }

            if (exists) {
              totalDelta = -1;
              return prev.filter((p) => p.id !== updatedPrayer.id);
            }
            return prev;
          });
          if (totalDelta !== 0) {
            setTotal((prev) => Math.max(0, prev + totalDelta));
          }
          break;

        case WSEventType.PRAYER_FLAGGED: {
          const flaggedPrayer = message.data as PrayerRequest;
          if (flaggedPrayer?.id) {
            setPrayers((prev) =>
              prev.map((p) =>
                p.id === flaggedPrayer.id ? { ...p, ...flaggedPrayer } : p
              )
            );
          }
          break;
        }

        case WSEventType.STAGE_ITEM_ADDED: {
          const added = message.data as {
            prayerRequestId?: string;
            prayer?: PrayerRequest;
          };
          const prayerId = added.prayer?.id ?? added.prayerRequestId;
          if (prayerId) {
            setPrayers((prev) =>
              prev.map((p) =>
                p.id === prayerId
                  ? { ...p, ...(added.prayer ?? {}), onStage: true }
                  : p
              )
            );
          }
          break;
        }

        case WSEventType.STAGE_ITEM_REMOVED: {
          const removed = message.data as { prayerRequestId?: string };
          if (removed.prayerRequestId) {
            setPrayers((prev) =>
              prev.map((p) =>
                p.id === removed.prayerRequestId ? { ...p, onStage: false } : p
              )
            );
          }
          break;
        }

        case WSEventType.STAGE_QUEUE_CLEARED: {
          const cleared = message.data as { prayerRequestIds?: string[] };
          const ids = new Set(cleared.prayerRequestIds ?? []);
          if (ids.size > 0) {
            setPrayers((prev) =>
              prev.map((p) => (ids.has(p.id) ? { ...p, onStage: false } : p))
            );
          }
          break;
        }

        case WSEventType.PRAYER_DISMISSED:
          // Update status - data is { id: string, reason?: string }
          const dismissedData = message.data as { id: string; reason?: string };
          const currentFiltersForDismissed = filtersRef.current;
          
          setPrayers((prev) => {
            const updated = prev.map((p) =>
              p.id === dismissedData.id ? { ...p, status: PrayerStatus.ARCHIVED } : p
            );
            
            // Remove from list if filter doesn't include archived prayers
            if (currentFiltersForDismissed.status !== 'all' && 
                currentFiltersForDismissed.status !== PrayerStatus.ARCHIVED) {
              const filtered = updated.filter((p) => p.id !== dismissedData.id);
              if (filtered.length !== updated.length) {
                setTotal((prevTotal) => Math.max(0, prevTotal - 1));
              }
              return filtered;
            }
            
            return updated;
          });
          break;

        case WSEventType.PRAYER_EDITING_STARTED: {
          const editData = message.data as { prayerId?: string; prayer_id?: string; userId?: string; user_id?: string; userName?: string; user_name?: string };
          const prayerId = editData.prayerId ?? editData.prayer_id;
          const userId = editData.userId ?? editData.user_id ?? '';
          const userName = editData.userName ?? editData.user_name ?? 'Unknown';
          if (prayerId) {
            setEditingBy((prev) => ({ ...prev, [prayerId]: { userId, userName } }));
          }
          break;
        }
        case WSEventType.PRAYER_EDITING_STOPPED: {
          const stopData = message.data as { prayerId?: string; prayer_id?: string };
          const prayerId = stopData.prayerId ?? stopData.prayer_id;
          if (prayerId) {
            setEditingBy((prev) => {
              const next = { ...prev };
              delete next[prayerId];
              return next;
            });
          }
          break;
        }

        case WSEventType.PRAYER_RESPONDING_STARTED: {
          const respData = message.data as { prayerId?: string; prayer_id?: string; userId?: string; user_id?: string; userName?: string; user_name?: string };
          const prayerId = respData.prayerId ?? respData.prayer_id;
          const userId = respData.userId ?? respData.user_id ?? '';
          const userName = respData.userName ?? respData.user_name ?? 'Unknown';
          if (prayerId) {
            setRespondingBy((prev) => ({ ...prev, [prayerId]: { userId, userName } }));
          }
          break;
        }
        case WSEventType.PRAYER_RESPONDING_STOPPED: {
          const stopData = message.data as { prayerId?: string; prayer_id?: string };
          const prayerId = stopData.prayerId ?? stopData.prayer_id;
          if (prayerId) {
            setRespondingBy((prev) => {
              const next = { ...prev };
              delete next[prayerId];
              return next;
            });
          }
          break;
        }

        case 'presence_snapshot':
        case 'presenceSnapshot': {
          const snap = message.data as {
            editing?: Record<
              string,
              { user_id?: string; userId?: string; user_name?: string; userName?: string }
            >;
            responding?: Record<
              string,
              { user_id?: string; userId?: string; user_name?: string; userName?: string }
            >;
          };
          const nextEditing: Record<string, { userId: string; userName: string }> = {};
          for (const [pid, row] of Object.entries(snap.editing ?? {})) {
            nextEditing[pid] = {
              userId: String(row.userId ?? row.user_id ?? ''),
              userName: String(row.userName ?? row.user_name ?? 'Unknown'),
            };
          }
          const nextResponding: Record<string, { userId: string; userName: string }> = {};
          for (const [pid, row] of Object.entries(snap.responding ?? {})) {
            nextResponding[pid] = {
              userId: String(row.userId ?? row.user_id ?? ''),
              userName: String(row.userName ?? row.user_name ?? 'Unknown'),
            };
          }
          setEditingBy(nextEditing);
          setRespondingBy(nextResponding);
          break;
        }

        case WSEventType.PRAYER_APPROVED:
          // Update status - data can be full prayer object or { id, status }
          const statusData = message.data as PrayerRequest | { id: string; status: PrayerStatus };
          const currentFiltersForStatus = filtersRef.current;
          
          if ('id' in statusData && 'status' in statusData && Object.keys(statusData).length === 2) {
            // Simple status update — merge with existing row so video filters stay accurate
            let totalDelta = 0;
            setPrayers((prev) => {
              const existing = prev.find((p) => p.id === statusData.id);
              if (!existing) return prev;
              const next: PrayerRequest = { ...existing, status: statusData.status };
              const matchesStatus = statusMatchesFilter(
                next.status,
                currentFiltersForStatus.status
              );
              const matchesVideo = videoResponseMatchesFilter(
                next,
                currentFiltersForStatus.videoResponseStatus
              );
              const matchesFilter = matchesStatus && matchesVideo;
              if (matchesFilter) {
                return prev.map((p) => (p.id === statusData.id ? next : p));
              }
              totalDelta = -1;
              return prev.filter((p) => p.id !== statusData.id);
            });
            if (totalDelta !== 0) {
              setTotal((prev) => Math.max(0, prev + totalDelta));
            }
          } else {
            // Full prayer object update
            const fullPrayer = statusData as PrayerRequest;
            const matchesStatus = statusMatchesFilter(
              fullPrayer.status,
              currentFiltersForStatus.status
            );
            const matchesVideo = videoResponseMatchesFilter(
              fullPrayer,
              currentFiltersForStatus.videoResponseStatus
            );
            const matchesType = currentFiltersForStatus.type === 'all' || 
                               fullPrayer.type === currentFiltersForStatus.type;
            const matchesCategory =
              currentFiltersForStatus.category === 'all' ||
              getPrayerCategories(fullPrayer).includes(currentFiltersForStatus.category);
            
            setPrayers((prev) => {
              const exists = prev.some((p) => p.id === fullPrayer.id);
              
              if (matchesStatus && matchesVideo && matchesType && matchesCategory) {
                return prev.map((p) => (p.id === fullPrayer.id ? fullPrayer : p));
              } else if (exists) {
                const filtered = prev.filter((p) => p.id !== fullPrayer.id);
                setTotal((prevTotal) => Math.max(0, prevTotal - 1));
                return filtered;
              }
              
              return prev;
            });
          }
          break;
      }
    },
    [] // No dependencies - use ref for filters
  );

  // Setup WebSocket connection (expose sendMessage for start_editing / stop_editing)
  const { sendMessage, isConnected, reconnectedTick } = useWebSocket({
    token,
    rooms: ['moderators'],
    onMessage: enableRealtime ? handleWebSocketMessage : undefined,
  });

  // Hydrate editing/responding locks on every connect (initial page load and reconnect).
  useEffect(() => {
    if (!enableRealtime || !isConnected) return;
    sendMessage({ action: 'get_presence' });
  }, [isConnected, enableRealtime, sendMessage]);

  useEffect(() => {
    if (!enableRealtime || reconnectedTick <= 0) return;
    fetchPrayersRef.current?.({ silent: true });
  }, [reconnectedTick, enableRealtime]);

  useEffect(() => {
    if (!enableRealtime || !autoFetch || isConnected) return;
    const id = setInterval(() => {
      fetchPrayersRef.current?.({ silent: true });
    }, 10_000);
    return () => clearInterval(id);
  }, [enableRealtime, autoFetch, isConnected]);

  // Store fetchPrayers in ref to avoid dependency issues
  useEffect(() => {
    fetchPrayersRef.current = fetchPrayers;
  }, [fetchPrayers]);

  // Auto-fetch on mount and when non-search filters/page change.
  useEffect(() => {
    if (autoFetch && !fetchingRef.current && fetchPrayersRef.current) {
      fetchPrayersRef.current();
    }
  }, [autoFetch, token, page, filters.status, filters.videoResponseStatus, filters.type, filters.category, filters.dateRange, filters.sortOrder]);

  // Re-run fetch silently when only the search term changes, so we update
  // the list without triggering global loading skeletons.
  useEffect(() => {
    if (autoFetch && !fetchingRef.current && fetchPrayersRef.current) {
      fetchPrayersRef.current({ silent: true });
    }
  }, [autoFetch, filters.search]);

  // Reset page when filters change (use individual filter values to avoid object reference issues)
  useEffect(() => {
    setPage(1);
  }, [filters.status, filters.videoResponseStatus, filters.type, filters.category, filters.dateRange, filters.search, filters.sortOrder]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const totalPages = Math.ceil(total / pageSize);

  return {
    prayers,
    total,
    page,
    totalPages,
    isLoading,
    error,
    filters,
    setFilters,
    setPage,
    refresh: () => fetchPrayers(),
    updatePrayer,
    dismissPrayer,
    queuePrayer,
    sendToStage,
    restorePrayer,
    queuingIds,
    stagingIds,
    isUpdating,
    editingBy,
    respondingBy,
    sendMessage,
    requestResponse,
    reportPrayer,
  };
}

export default usePrayerRequests;
